/**
 * Blobbi Tag Schema - Canonical Reference
 * 
 * This file defines the single source of truth for all Blobbi tags used in Kind 31124
 * (Blobbi State) events. It documents which tags exist, their purpose, when they're
 * used, and how they should be handled during stage transitions.
 * 
 * @module blobbi-tag-schema
 */

import type { BlobbiStage } from './blobbi';

// ─── Tag Source Types ─────────────────────────────────────────────────────────

/**
 * Where the tag value originates from.
 * 
 * - 'system': Generated by the system (d, b, t, client, seed)
 * - 'generated': Derived deterministically from seed or other data
 * - 'user': Set by user action or input
 * - 'computed': Calculated from game state or events
 */
export type TagSource = 'system' | 'generated' | 'user' | 'computed';

/**
 * Tag category for grouping related tags.
 */
export type TagCategory =
  | 'system'           // Protocol-level tags (d, b, t, client)
  | 'identity'         // Core identity (name, seed, generation)
  | 'visual'           // Visual traits (colors, pattern, size)
  | 'personality'      // Personality/traits (mood, favorite_food)
  | 'stats'            // Numeric stats (hunger, health, etc.)
  | 'state'            // Lifecycle state (stage, state, timestamps)
  | 'progression'      // Progress tracking (experience, care_streak)
  | 'task'             // Task system (task, task_completed, progression_state, progression_started_at)
  | 'social'           // Social flags (breeding_ready)
  | 'evolution'        // Evolution-specific (adult_type)
  | 'extension';       // Extension tags (theme, crossover_app)

// ─── Tag Schema Definition ────────────────────────────────────────────────────

/**
 * Schema definition for a single Blobbi tag.
 */
export interface BlobbiTagSchema {
  /** Tag name (the first element of the tag array) */
  tag: string;
  
  /** Human-readable description of what this tag represents */
  description: string;
  
  /** Category for grouping */
  category: TagCategory;
  
  /** Whether this tag is required for a valid Blobbi event */
  required: boolean;
  
  /** Which stages this tag should be present in */
  stages: BlobbiStage[];
  
  /** Whether this tag persists across stage transitions (egg → baby → adult) */
  persistent: boolean;
  
  /** Where the tag value comes from */
  source: TagSource;
  
  /** Whether the tag can be regenerated from seed or must be preserved */
  regenerable: boolean;
  
  /** Expected format or valid values */
  format?: string;
  
  /** Default value if not present */
  defaultValue?: string;
  
  /** Notes about special handling or behavior */
  notes?: string;
}

// ─── Canonical Tag Schema ─────────────────────────────────────────────────────

/**
 * Complete canonical schema for all valid Blobbi tags.
 * 
 * This is the SINGLE SOURCE OF TRUTH for Blobbi tag definitions.
 * All stage transitions, migrations, and validations should reference this schema.
 */
export const BLOBBI_TAG_SCHEMA: readonly BlobbiTagSchema[] = [
  // ═══════════════════════════════════════════════════════════════════════════
  // SYSTEM / METADATA TAGS
  // ═══════════════════════════════════════════════════════════════════════════
  {
    tag: 'd',
    description: 'Unique identifier for the Blobbi (addressable event d-tag)',
    category: 'system',
    required: true,
    stages: ['egg', 'baby', 'adult'],
    persistent: true,
    source: 'system',
    regenerable: false,
    format: 'blobbi-{pubkeyPrefix12}-{petId10}',
    notes: 'Canonical format required. Legacy formats trigger migration.',
  },
  {
    tag: 'b',
    description: 'Ecosystem namespace identifier',
    category: 'system',
    required: true,
    stages: ['egg', 'baby', 'adult'],
    persistent: true,
    source: 'system',
    regenerable: true,
    format: 'blobbi:ecosystem:v1',
    defaultValue: 'blobbi:ecosystem:v1',
  },
  // ═══════════════════════════════════════════════════════════════════════════
  // CORE IDENTITY TAGS
  // ═══════════════════════════════════════════════════════════════════════════
  {
    tag: 'name',
    description: 'Display name for the Blobbi',
    category: 'identity',
    required: true,
    stages: ['egg', 'baby', 'adult'],
    persistent: true,
    source: 'user',
    regenerable: false,
    notes: 'Set during adoption. Can be changed by user. Must be preserved across stages.',
  },
  {
    tag: 'seed',
    description: '64-character hex seed for deterministic visual trait generation',
    category: 'identity',
    required: true,
    stages: ['egg', 'baby', 'adult'],
    persistent: true,
    source: 'system',
    regenerable: false,
    format: '64 lowercase hex characters',
    notes: 'Derived once at creation: sha256("blobbi:v1|{pubkey}:{d}:{createdAt}"). MUST NOT be recomputed.',
  },
  {
    tag: 'generation',
    description: 'Generation number in the Blobbi lineage',
    category: 'identity',
    required: false,
    stages: ['egg', 'baby', 'adult'],
    persistent: true,
    source: 'system',
    regenerable: false,
    format: 'positive integer',
    defaultValue: '1',
    notes: 'Starts at 1 for adopted Blobbis. Increments for bred offspring.',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // VISUAL TRAIT TAGS
  // ═══════════════════════════════════════════════════════════════════════════
  {
    tag: 'base_color',
    description: 'Primary/base color for the Blobbi',
    category: 'visual',
    required: false,
    stages: ['egg', 'baby', 'adult'],
    persistent: true,
    source: 'generated',
    regenerable: true,
    format: 'CSS hex color (e.g., #F59E0B)',
    notes: 'Derived from seed. Stored explicitly for fast rendering and legacy compatibility.',
  },
  {
    tag: 'secondary_color',
    description: 'Secondary/accent color for the Blobbi',
    category: 'visual',
    required: false,
    stages: ['egg', 'baby', 'adult'],
    persistent: true,
    source: 'generated',
    regenerable: true,
    format: 'CSS hex color (e.g., #FCD34D)',
    notes: 'Derived from seed.',
  },
  {
    tag: 'eye_color',
    description: 'Eye color for the Blobbi',
    category: 'visual',
    required: false,
    stages: ['egg', 'baby', 'adult'],
    persistent: true,
    source: 'generated',
    regenerable: true,
    format: 'CSS hex color (e.g., #1F2937)',
    notes: 'Derived from seed.',
  },
  {
    tag: 'pattern',
    description: 'Visual pattern type',
    category: 'visual',
    required: false,
    stages: ['egg', 'baby', 'adult'],
    persistent: true,
    source: 'generated',
    regenerable: true,
    format: 'solid | spotted | striped | gradient',
    notes: 'Derived from seed.',
  },
  {
    tag: 'special_mark',
    description: 'Special decorative marking',
    category: 'visual',
    required: false,
    stages: ['egg', 'baby', 'adult'],
    persistent: true,
    source: 'generated',
    regenerable: true,
    format: 'none | star | heart | sparkle | blush',
    notes: 'Derived from seed.',
  },
  {
    tag: 'size',
    description: 'Size category',
    category: 'visual',
    required: false,
    stages: ['egg', 'baby', 'adult'],
    persistent: true,
    source: 'generated',
    regenerable: true,
    format: 'small | medium | large',
    notes: 'Derived from seed.',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // PERSONALITY / TRAIT TAGS
  // ═══════════════════════════════════════════════════════════════════════════
  {
    tag: 'personality',
    description: 'Core personality type that influences behavior',
    category: 'personality',
    required: false,
    stages: ['egg', 'baby', 'adult'],
    persistent: true,
    source: 'generated',
    regenerable: false,
    notes: 'Generated at creation. MUST persist across all stage transitions.',
  },
  {
    tag: 'trait',
    description: 'Character trait modifier',
    category: 'personality',
    required: false,
    stages: ['egg', 'baby', 'adult'],
    persistent: true,
    source: 'generated',
    regenerable: false,
    notes: 'Generated at creation. MUST persist across all stage transitions.',
  },
  {
    tag: 'favorite_food',
    description: 'Preferred food type (affects happiness from feeding)',
    category: 'personality',
    required: false,
    stages: ['egg', 'baby', 'adult'],
    persistent: true,
    source: 'generated',
    regenerable: false,
    notes: 'Generated at creation. MUST persist across all stage transitions.',
  },
  {
    tag: 'voice_type',
    description: 'Voice characteristic for audio feedback',
    category: 'personality',
    required: false,
    stages: ['egg', 'baby', 'adult'],
    persistent: true,
    source: 'generated',
    regenerable: false,
    notes: 'Generated at creation. MUST persist across all stage transitions.',
  },
  {
    tag: 'mood',
    description: 'Current emotional state',
    category: 'personality',
    required: false,
    stages: ['egg', 'baby', 'adult'],
    persistent: true,
    source: 'computed',
    regenerable: false,
    notes: 'May change based on stats and interactions. Persists across stages.',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // STAT TAGS
  // ═══════════════════════════════════════════════════════════════════════════
  {
    tag: 'hunger',
    description: 'Hunger level (higher = more full)',
    category: 'stats',
    required: false,
    stages: ['egg', 'baby', 'adult'],
    persistent: false,
    source: 'computed',
    regenerable: false,
    format: '1-100 integer',
    defaultValue: '100',
    notes: 'Decays over time. Eggs have fixed value of 100.',
  },
  {
    tag: 'happiness',
    description: 'Happiness level',
    category: 'stats',
    required: false,
    stages: ['egg', 'baby', 'adult'],
    persistent: false,
    source: 'computed',
    regenerable: false,
    format: '1-100 integer',
    defaultValue: '100',
    notes: 'Affected by interactions and care quality.',
  },
  {
    tag: 'health',
    description: 'Health level',
    category: 'stats',
    required: false,
    stages: ['egg', 'baby', 'adult'],
    persistent: false,
    source: 'computed',
    regenerable: false,
    format: '1-100 integer',
    defaultValue: '100',
    notes: 'Eggs inherit health to baby. Baby inherits health to adult.',
  },
  {
    tag: 'hygiene',
    description: 'Hygiene/cleanliness level',
    category: 'stats',
    required: false,
    stages: ['egg', 'baby', 'adult'],
    persistent: false,
    source: 'computed',
    regenerable: false,
    format: '1-100 integer',
    defaultValue: '100',
    notes: 'Decays over time. Affects health if too low.',
  },
  {
    tag: 'energy',
    description: 'Energy level',
    category: 'stats',
    required: false,
    stages: ['egg', 'baby', 'adult'],
    persistent: false,
    source: 'computed',
    regenerable: false,
    format: '1-100 integer',
    defaultValue: '100',
    notes: 'Restored by sleeping. Eggs have fixed value of 100.',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // STATE / LIFECYCLE TAGS
  // ═══════════════════════════════════════════════════════════════════════════
  {
    tag: 'stage',
    description: 'Current lifecycle stage',
    category: 'state',
    required: true,
    stages: ['egg', 'baby', 'adult'],
    persistent: false,
    source: 'system',
    regenerable: false,
    format: 'egg | baby | adult',
    notes: 'Changes during hatch (egg→baby) and evolve (baby→adult).',
  },
  {
    tag: 'state',
    description: 'Current activity state',
    category: 'state',
    required: true,
    stages: ['egg', 'baby', 'adult'],
    persistent: false,
    source: 'system',
    regenerable: false,
    format: 'active | sleeping | hibernating',
    notes: 'Activity state only. Progression (incubation/evolution) is tracked in progression_state.',
  },
  {
    tag: 'last_interaction',
    description: 'Unix timestamp of last user interaction',
    category: 'state',
    required: true,
    stages: ['egg', 'baby', 'adult'],
    persistent: false,
    source: 'system',
    regenerable: false,
    format: 'Unix timestamp (seconds)',
    notes: 'Updated on any user action. Used for activity tracking.',
  },
  {
    tag: 'last_decay_at',
    description: 'Unix timestamp for stat decay checkpoint',
    category: 'state',
    required: false,
    stages: ['egg', 'baby', 'adult'],
    persistent: false,
    source: 'system',
    regenerable: false,
    format: 'Unix timestamp (seconds)',
    notes: 'Used to calculate accumulated decay. Reset after applying decay.',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // TASK SYSTEM TAGS
  // ═══════════════════════════════════════════════════════════════════════════
  {
    tag: 'progression_state',
    description: 'Current progression process state (orthogonal to activity state)',
    category: 'task',
    required: false,
    stages: ['egg', 'baby', 'adult'],
    persistent: false,
    source: 'system',
    regenerable: false,
    format: 'none | incubating | evolving',
    defaultValue: 'none',
    notes: 'Set to incubating/evolving when a progression process starts. Cleared to none after hatch/evolve completes. Survives sleep/wake/hibernation changes.',
  },
  {
    tag: 'progression_started_at',
    description: 'Unix timestamp when current progression (incubating/evolving) started',
    category: 'task',
    required: false,
    stages: ['egg', 'baby', 'adult'],
    persistent: false,
    source: 'system',
    regenerable: false,
    format: 'Unix timestamp (seconds)',
    notes: 'Set when entering incubating/evolving. REMOVED after hatch/evolve completes.',
  },
  {
    tag: 'state_started_at',
    description: '@deprecated Use progression_started_at instead. Unix timestamp when current state (incubating/evolving) started',
    category: 'task',
    required: false,
    stages: ['egg', 'baby', 'adult'],
    persistent: false,
    source: 'system',
    regenerable: false,
    format: 'Unix timestamp (seconds)',
    notes: 'Legacy tag. New events should use progression_started_at instead.',
  },
  {
    tag: 'task',
    description: 'Task progress tracking',
    category: 'task',
    required: false,
    stages: ['egg', 'baby', 'adult'],
    persistent: false,
    source: 'computed',
    regenerable: false,
    format: '["task", "taskName:progressValue"]',
    notes: 'Multiple task tags allowed. REMOVED after stage transition completes.',
  },
  {
    tag: 'task_completed',
    description: 'Completed task marker',
    category: 'task',
    required: false,
    stages: ['egg', 'baby', 'adult'],
    persistent: false,
    source: 'computed',
    regenerable: false,
    format: '["task_completed", "taskName"]',
    notes: 'Multiple allowed. REMOVED after stage transition completes.',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // PROGRESSION TAGS
  // ═══════════════════════════════════════════════════════════════════════════
  {
    tag: 'experience',
    description: 'Total experience points accumulated',
    category: 'progression',
    required: false,
    stages: ['egg', 'baby', 'adult'],
    persistent: true,
    source: 'computed',
    regenerable: false,
    format: 'non-negative integer',
    defaultValue: '0',
    notes: 'Accumulates across all stages. Never resets.',
  },
  {
    tag: 'care_streak',
    description: 'Consecutive days of care',
    category: 'progression',
    required: false,
    stages: ['egg', 'baby', 'adult'],
    persistent: true,
    source: 'computed',
    regenerable: false,
    format: 'positive integer',
    defaultValue: '1',
    notes: 'Starts at 1 on first activity. Increments when activity occurs on the next calendar day. Resets to 1 if 2+ days are missed. Persists across stages.',
  },
  {
    tag: 'care_streak_last_at',
    description: 'Unix timestamp (seconds) of last streak update',
    category: 'progression',
    required: false,
    stages: ['egg', 'baby', 'adult'],
    persistent: true,
    source: 'computed',
    regenerable: false,
    format: 'unix timestamp (seconds)',
    defaultValue: undefined,
    notes: 'Stores when the streak was last updated. Used alongside care_streak_last_day for debugging and validation.',
  },
  {
    tag: 'care_streak_last_day',
    description: 'Local calendar day (YYYY-MM-DD) of last streak update',
    category: 'progression',
    required: false,
    stages: ['egg', 'baby', 'adult'],
    persistent: true,
    source: 'computed',
    regenerable: false,
    format: 'YYYY-MM-DD',
    defaultValue: undefined,
    notes: 'Stores the local calendar day when streak was last counted. Used to prevent double-counting on the same day and to calculate days missed.',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // SOCIAL / FLAG TAGS
  // ═══════════════════════════════════════════════════════════════════════════
  {
    tag: 'social',
    description: 'Whether external users can interact with this Blobbi via kind 1124 events',
    category: 'social',
    required: false,
    stages: ['egg', 'baby', 'adult'],
    persistent: true,
    source: 'user',
    regenerable: false,
    format: 'open | closed',
    defaultValue: 'closed',
    notes: 'Controls the external social interaction gate. Absent or "closed" = denied. Owner interactions bypass this check.',
  },
  {
    tag: 'breeding_ready',
    description: 'Whether the Blobbi is eligible for breeding',
    category: 'social',
    required: false,
    stages: ['egg', 'baby', 'adult'],
    persistent: true,
    source: 'computed',
    regenerable: false,
    format: 'true | false',
    defaultValue: 'false',
    notes: 'Typically only true for healthy adults.',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // EVOLUTION TAGS
  // ═══════════════════════════════════════════════════════════════════════════
  {
    tag: 'adult_type',
    description: 'Adult evolution form type',
    category: 'evolution',
    required: false,
    stages: ['adult'],
    persistent: true,
    source: 'computed',
    regenerable: false,
    notes: 'Only present for adults. Determined during evolution based on care history.',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // EXTENSION TAGS (Optional, for crossovers/themes)
  // ═══════════════════════════════════════════════════════════════════════════
  {
    tag: 'theme',
    description: 'Theme variant identifier',
    category: 'extension',
    required: false,
    stages: ['egg', 'baby', 'adult'],
    persistent: true,
    source: 'system',
    regenerable: false,
    format: 'Theme identifier string (e.g., "divine")',
    notes: 'Used for special themed Blobbis. Persists across stages.',
  },
  {
    tag: 'crossover_app',
    description: 'Crossover application identifier',
    category: 'extension',
    required: false,
    stages: ['egg', 'baby', 'adult'],
    persistent: true,
    source: 'system',
    regenerable: false,
    format: 'Application identifier string (e.g., "divine")',
    notes: 'Indicates Blobbi originated from or has features from another app.',
  },
] as const;

// ─── Deprecated Tags ──────────────────────────────────────────────────────────

/**
 * Schema for deprecated tags that should be removed when republishing.
 * These tags were part of earlier designs but are no longer used.
 */
export interface DeprecatedTagSchema {
  /** Tag name */
  tag: string;
  /** Why this tag was deprecated */
  reason: string;
  /** What replaced it (if applicable) */
  replacedBy?: string;
  /** When it was deprecated (version or date) */
  deprecatedSince: string;
}

/**
 * List of deprecated tags that should be filtered out during republishing.
 */
export const DEPRECATED_TAG_SCHEMA: readonly DeprecatedTagSchema[] = [
  {
    tag: 't',
    reason: 'Topic tag no longer needed - the app uses the b namespace tag for identification',
    replacedBy: undefined,
    deprecatedSince: 'v2.0',
  },
  {
    tag: 'client',
    reason: 'Client tag no longer needed - useNostrPublish adds this automatically',
    replacedBy: undefined,
    deprecatedSince: 'v2.0',
  },
  {
    tag: 'shell_integrity',
    reason: 'Eggs now use the standard health stat instead of a separate shell metric',
    replacedBy: 'health',
    deprecatedSince: 'v1.0',
  },
  {
    tag: 'egg_temperature',
    reason: 'Egg warmth is handled via UI props, not persisted state',
    replacedBy: undefined,
    deprecatedSince: 'v1.0',
  },
  {
    tag: 'incubation_progress',
    reason: 'Replaced by task-based incubation system',
    replacedBy: 'task, task_completed',
    deprecatedSince: 'v1.0',
  },
  {
    tag: 'egg_status',
    reason: 'Replaced by standard state field',
    replacedBy: 'state',
    deprecatedSince: 'v1.0',
  },
  {
    tag: 'fees',
    reason: 'Fee tracking removed from Blobbi events',
    replacedBy: undefined,
    deprecatedSince: 'v1.0',
  },
  {
    tag: 'incubation_time',
    reason: 'Task system uses progression_started_at for timing',
    replacedBy: 'progression_started_at',
    deprecatedSince: 'v1.0',
  },
  {
    tag: 'start_incubation',
    reason: 'Task system uses progression_started_at for timing',
    replacedBy: 'progression_started_at',
    deprecatedSince: 'v1.0',
  },
  {
    tag: 'interact_6_progress',
    reason: 'Legacy interaction tracking from old system',
    replacedBy: '["task", "interactions:N"]',
    deprecatedSince: 'v1.1',
  },
] as const;

// ─── Helper Functions ─────────────────────────────────────────────────────────

/**
 * Get all tag names that should persist across stage transitions.
 */
export function getPersistentTagNames(): Set<string> {
  return new Set(
    BLOBBI_TAG_SCHEMA
      .filter(schema => schema.persistent)
      .map(schema => schema.tag)
  );
}

/**
 * Get all tag names that should be removed during stage transitions.
 * These are task-related and state-specific tags.
 */
export function getTransitionCleanupTagNames(): Set<string> {
  return new Set(['task', 'task_completed', 'state_started_at', 'progression_started_at', 'progression_state']);
}

/**
 * Get all deprecated tag names.
 */
export function getDeprecatedTagNames(): Set<string> {
  return new Set(DEPRECATED_TAG_SCHEMA.map(schema => schema.tag));
}

/**
 * Get all required tag names.
 */
export function getRequiredTagNames(): Set<string> {
  return new Set(
    BLOBBI_TAG_SCHEMA
      .filter(schema => schema.required)
      .map(schema => schema.tag)
  );
}

/**
 * Get tag schema by tag name.
 */
export function getTagSchema(tagName: string): BlobbiTagSchema | undefined {
  return BLOBBI_TAG_SCHEMA.find(schema => schema.tag === tagName);
}

/**
 * Get all tags for a specific stage.
 */
export function getTagsForStage(stage: BlobbiStage): BlobbiTagSchema[] {
  return BLOBBI_TAG_SCHEMA.filter(schema => schema.stages.includes(stage));
}

/**
 * Get all tags in a specific category.
 */
export function getTagsByCategory(category: TagCategory): BlobbiTagSchema[] {
  return BLOBBI_TAG_SCHEMA.filter(schema => schema.category === category);
}

/**
 * Validate that an event has all required tags.
 */
export function validateRequiredTags(tags: string[][]): { valid: boolean; missing: string[] } {
  const requiredTags = getRequiredTagNames();
  const presentTags = new Set(tags.map(tag => tag[0]));
  const missing = [...requiredTags].filter(tag => !presentTags.has(tag));
  
  return {
    valid: missing.length === 0,
    missing,
  };
}

// ─── Tag Integrity Guard ──────────────────────────────────────────────────────

/**
 * Result of tag validation and repair.
 */
export interface TagRepairResult {
  /** The repaired tags (ready for publishing) */
  tags: string[][];
  /** Whether any repairs were made */
  repaired: boolean;
  /** List of repairs that were made */
  repairs: string[];
  /** List of issues that could not be repaired */
  errors: string[];
  /** The detected final stage */
  finalStage: BlobbiStage | null;
}

/**
 * System-required tags that can be recovered with default values.
 * These are protocol-level tags that have well-defined defaults.
 */
const RECOVERABLE_SYSTEM_TAGS: Record<string, string> = {
  b: 'blobbi:ecosystem:v1',
};

/**
 * Tags that should NEVER be invented if they don't exist.
 * Per spec: Do NOT invent personality/trait/adult_type for existing Blobbis.
 */
const NEVER_INVENT_TAGS = new Set([
  // Personality tags - generated at creation only
  'personality',
  'trait',
  'favorite_food',
  'voice_type',
  'mood',
  // Evolution tags - computed during evolve only
  'adult_type',
  // Extension tags - set by specific features only
  'theme',
  'crossover_app',
  // Identity tags that are user-set or derived once
  'name',
  'seed',
  'd',
]);

/**
 * Valid states for each stage. Used to validate/repair state after transitions.
 */
const VALID_STATES_BY_STAGE: Record<BlobbiStage, Set<string>> = {
  egg: new Set(['active', 'sleeping', 'hibernating']),
  baby: new Set(['active', 'sleeping', 'hibernating']),
  adult: new Set(['active', 'sleeping', 'hibernating']),
};

/**
 * Task-process states that should NOT remain in the `state` tag after stage transitions.
 * With the new model, state should never be 'incubating'/'evolving' — but we keep this
 * for migration: if a legacy event has them in state, the repair will fix it.
 */
const TASK_PROCESS_STATES = new Set(['incubating', 'evolving']);

/**
 * Validate and repair Blobbi tags before publishing.
 * 
 * This function ensures tag integrity by:
 * 1. Removing deprecated tags
 * 2. Removing task tags when cleanupTaskTags is true
 * 3. Stage-aware validation: removes tags not valid for the final stage
 * 4. Semantic cleanup: ensures state is valid after transitions
 * 5. Recovering missing required system tags (b, t, client) with defaults
 * 6. Recovering missing required tags from previousTags if available
 * 7. Preserving persistent tags that are valid for the final stage
 * 8. NEVER inventing personality/trait/adult_type values
 * 
 * @param tags - The current tags to validate and repair
 * @param previousTags - Optional previous canonical tags for recovery
 * @param options - Optional configuration for repair behavior
 * @returns TagRepairResult with repaired tags and repair log
 * 
 * @example
 * ```typescript
 * const result = validateAndRepairBlobbiTags(newTags, canonical.allTags, { cleanupTaskTags: true });
 * if (result.errors.length > 0) {
 *   console.error('Cannot publish:', result.errors);
 *   return;
 * }
 * await publishEvent({ kind: KIND_BLOBBI_STATE, tags: result.tags, ... });
 * ```
 */
export function validateAndRepairBlobbiTags(
  tags: string[][],
  previousTags?: string[][],
  options?: {
    /** If true, removes task-related tags (for stage transitions) */
    cleanupTaskTags?: boolean;
  }
): TagRepairResult {
  const repairs: string[] = [];
  const errors: string[] = [];
  const isDev = import.meta.env.DEV;
  
  // Build a map of current tags for quick lookup
  const tagMap = new Map<string, string[][]>();
  for (const tag of tags) {
    const name = tag[0];
    if (!tagMap.has(name)) {
      tagMap.set(name, []);
    }
    tagMap.get(name)!.push(tag);
  }
  
  // Build a map of previous tags for recovery
  const previousTagMap = new Map<string, string>();
  if (previousTags) {
    for (const tag of previousTags) {
      // For single-value tags, just store the last value
      // Multi-value tags (task, task_completed) are handled specially
      if (tag[0] !== 'task' && tag[0] !== 'task_completed') {
        previousTagMap.set(tag[0], tag[1]);
      }
    }
  }
  
  // Get deprecated and task cleanup tag sets
  const deprecatedTags = getDeprecatedTagNames();
  const taskCleanupTags = options?.cleanupTaskTags ? getTransitionCleanupTagNames() : new Set<string>();
  
  // ─── Step 1: Filter out deprecated tags and task tags (if cleanup requested) ───
  let filteredTags: string[][] = [];
  for (const tag of tags) {
    const name = tag[0];
    
    if (deprecatedTags.has(name)) {
      repairs.push(`Removed deprecated tag: ${name}`);
      continue;
    }
    
    if (taskCleanupTags.has(name)) {
      repairs.push(`Removed task tag during transition: ${name}`);
      continue;
    }
    
    filteredTags.push(tag);
  }
  
  // ─── Step 2: Detect the final stage ───
  const stageTag = filteredTags.find(t => t[0] === 'stage');
  const finalStage = (stageTag?.[1] as BlobbiStage) || null;
  
  if (!finalStage || !['egg', 'baby', 'adult'].includes(finalStage)) {
    errors.push(`Invalid or missing stage tag: '${finalStage}'`);
    // Cannot proceed with stage-aware validation without a valid stage
    return {
      tags: filteredTags,
      repaired: repairs.length > 0,
      repairs,
      errors,
      finalStage: null,
    };
  }
  
  // ─── Step 3: Stage-aware tag filtering ───
  // Remove tags that are not valid for the final stage
  const stageFilteredTags: string[][] = [];
  
  for (const tag of filteredTags) {
    const name = tag[0];
    
    // Check if this tag is valid for the current stage
    const schema = getTagSchema(name);
    if (schema && !schema.stages.includes(finalStage)) {
      repairs.push(`Removed tag '${name}' (not valid for stage '${finalStage}')`);
      if (isDev) {
        console.warn(`[Blobbi] Removed invalid-for-stage tag: ${name} (stage: ${finalStage})`);
      }
      continue;
    }
    
    stageFilteredTags.push(tag);
  }
  
  filteredTags = stageFilteredTags;
  
  // ─── Step 4: Semantic cleanup - validate state after transitions ───
  if (options?.cleanupTaskTags) {
    const stateTagIndex = filteredTags.findIndex(t => t[0] === 'state');
    if (stateTagIndex !== -1) {
      const currentState = filteredTags[stateTagIndex][1];
      
      // After hatch/evolve, state must not be a task-process state
      if (TASK_PROCESS_STATES.has(currentState)) {
        filteredTags[stateTagIndex] = ['state', 'active'];
        repairs.push(`Repaired state from '${currentState}' to 'active' (task process completed)`);
        if (isDev) {
          console.warn(`[Blobbi] Fixed invalid state '${currentState}' -> 'active' after transition`);
        }
      }
      
      // Validate state is valid for the stage
      const validStates = VALID_STATES_BY_STAGE[finalStage];
      const newState = filteredTags[stateTagIndex][1];
      if (!validStates.has(newState)) {
        filteredTags[stateTagIndex] = ['state', 'active'];
        repairs.push(`Repaired invalid state '${newState}' to 'active' for stage '${finalStage}'`);
        if (isDev) {
          console.warn(`[Blobbi] Fixed invalid state '${newState}' -> 'active' for stage ${finalStage}`);
        }
      }
    }
  }
  
  // Rebuild tag map after filtering
  tagMap.clear();
  for (const tag of filteredTags) {
    const name = tag[0];
    if (!tagMap.has(name)) {
      tagMap.set(name, []);
    }
    tagMap.get(name)!.push(tag);
  }
  
  // ─── Step 5: Check for required tags and attempt recovery ───
  const requiredTags = getRequiredTagNames();
  const repairedTags = [...filteredTags];
  
  for (const requiredTag of requiredTags) {
    // Skip required tags that aren't valid for this stage
    const schema = getTagSchema(requiredTag);
    if (schema && !schema.stages.includes(finalStage)) {
      continue;
    }
    
    if (tagMap.has(requiredTag)) {
      // Tag exists, validate it's not empty
      const tagValue = tagMap.get(requiredTag)![0]?.[1];
      if (!tagValue || tagValue.trim() === '') {
        // Try to recover from previous tags or defaults
        const recovered = recoverTagValue(requiredTag, previousTagMap);
        if (recovered !== null) {
          // Remove the empty tag and add recovered value
          const index = repairedTags.findIndex(t => t[0] === requiredTag);
          if (index !== -1) {
            repairedTags[index] = [requiredTag, recovered];
            const source = previousTagMap.has(requiredTag) ? 'previous tags' : 'defaults';
            repairs.push(`Repaired empty ${requiredTag} tag from ${source}`);
          }
        } else {
          errors.push(`Required tag '${requiredTag}' is empty and cannot be recovered`);
        }
      }
    } else {
      // Tag is missing, attempt recovery
      const recovered = recoverTagValue(requiredTag, previousTagMap);
      if (recovered !== null) {
        repairedTags.push([requiredTag, recovered]);
        const source = previousTagMap.has(requiredTag) ? 'previous tags' : 'defaults';
        repairs.push(`Recovered missing ${requiredTag} tag from ${source}`);
      } else {
        errors.push(`Required tag '${requiredTag}' is missing and cannot be recovered`);
      }
    }
  }
  
  // ─── Step 6: Recover persistent tags from previous tags (if they existed before) ───
  // Only recover tags that are valid for the final stage
  // NEVER invent values for tags in NEVER_INVENT_TAGS
  if (previousTags) {
    const persistentTags = getPersistentTagNames();
    const currentTagNames = new Set(repairedTags.map(t => t[0]));
    
    for (const [tagName, tagValue] of previousTagMap.entries()) {
      // Skip if already present in current tags
      if (currentTagNames.has(tagName)) continue;
      
      // Skip non-persistent tags
      if (!persistentTags.has(tagName)) continue;
      
      // Skip task-related tags if cleanup is requested
      if (taskCleanupTags.has(tagName)) continue;
      
      // Skip tags that are not valid for the final stage
      const schema = getTagSchema(tagName);
      if (schema && !schema.stages.includes(finalStage)) {
        if (isDev) {
          console.warn(`[Blobbi] Skipped recovering '${tagName}' (not valid for stage '${finalStage}')`);
        }
        continue;
      }
      
      // Recover the persistent tag
      repairedTags.push([tagName, tagValue]);
      repairs.push(`Recovered persistent tag: ${tagName}`);
    }
  }
  
  // ─── Final dev diagnostics ───
  if (isDev && repairs.length > 0) {
    console.warn('[Blobbi] Tag repairs applied:', repairs);
  }
  
  return {
    tags: repairedTags,
    repaired: repairs.length > 0,
    repairs,
    errors,
    finalStage,
  };
}

/**
 * Attempt to recover a tag value from previous tags or system defaults.
 * 
 * Recovery strategy:
 * 1. If tag exists in previousTags, use that value
 * 2. If tag is a recoverable system tag, use the default
 * 3. Otherwise, return null (cannot recover)
 * 
 * NEVER invents values for personality/trait/adult_type tags.
 */
function recoverTagValue(
  tagName: string,
  previousTagMap: Map<string, string>
): string | null {
  // First, try to recover from previous tags
  if (previousTagMap.has(tagName)) {
    const value = previousTagMap.get(tagName)!;
    if (value && value.trim() !== '') {
      return value;
    }
  }
  
  // Never invent certain tags
  if (NEVER_INVENT_TAGS.has(tagName)) {
    return null;
  }
  
  // Try system defaults for recoverable tags
  if (tagName in RECOVERABLE_SYSTEM_TAGS) {
    return RECOVERABLE_SYSTEM_TAGS[tagName];
  }
  
  return null;
}

/**
 * Check if tags pass validation without repairing.
 * Use this for quick validation checks.
 */
export function isValidBlobbiTagSet(tags: string[][]): boolean {
  const result = validateAndRepairBlobbiTags(tags);
  return result.errors.length === 0;
}

// ─── Schema Summary for Documentation ─────────────────────────────────────────

/**
 * Generate a summary of the tag schema grouped by category.
 * Useful for documentation and debugging.
 */
export function generateSchemaDocumentation(): string {
  const categories = [
    'system', 'identity', 'visual', 'personality', 'stats',
    'state', 'task', 'progression', 'social', 'evolution', 'extension',
  ] as TagCategory[];
  
  let doc = '# Blobbi Tag Schema\n\n';
  
  for (const category of categories) {
    const tags = getTagsByCategory(category);
    if (tags.length === 0) continue;
    
    doc += `## ${category.charAt(0).toUpperCase() + category.slice(1)} Tags\n\n`;
    doc += '| Tag | Required | Stages | Persistent | Source | Description |\n';
    doc += '|-----|----------|--------|------------|--------|-------------|\n';
    
    for (const tag of tags) {
      const stages = tag.stages.join(', ');
      doc += `| \`${tag.tag}\` | ${tag.required ? 'Yes' : 'No'} | ${stages} | ${tag.persistent ? 'Yes' : 'No'} | ${tag.source} | ${tag.description} |\n`;
    }
    
    doc += '\n';
  }
  
  doc += '## Deprecated Tags\n\n';
  doc += '| Tag | Reason | Replaced By |\n';
  doc += '|-----|--------|-------------|\n';
  
  for (const tag of DEPRECATED_TAG_SCHEMA) {
    doc += `| \`${tag.tag}\` | ${tag.reason} | ${tag.replacedBy ?? 'N/A'} |\n`;
  }
  
  return doc;
}
